import { UserApi } from 'api/user/user-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { AppError } from 'lib/error/error.models.js';
import { isNotUndefined } from 'lib/helpers/array.utils.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { Observable, map } from 'rxjs';
import { ClientDashboardComponent } from 'src/app/modules/home/client-dashboard.component.js';
import { ClientSessionsComponent } from 'src/app/modules/users/sessions/client-sessions.component.js';
import { CoreAppComponentDependencies } from '../../core/core-app-dependencies.js';
import { CalendarComponent } from '../../modules/calendar/calendar.component.js';
import { ViewOrEditDietComponent } from '../../modules/diets/view-or-edit-diet.component.js';
import { foodHelper } from '../../modules/foods/food.helper.js';
import { ClientFilesComponent } from '../../modules/users/files/client-files.component.js';
import { ClientGalleryComponent } from '../../modules/users/gallery/client-gallery.component.js';
import { ClientDietComponent } from '../../modules/users/nutrition/client-diet.component.js';
import { ClientProgressComponent } from '../../modules/users/progress/client-progress.component.js';
import { ClientTasksComponent } from '../../modules/users/tasks/client-tasks.component.js';
import { ClientTrainingComponent } from '../../modules/users/training/client-training.component.js';
import { ViewOrEditWorkoutComponent } from '../../modules/workouts/view-or-edit-workout.component.js';
import {
	ICoreRoutes,
	ILoadSharedDataContext,
	IPageMetadata,
	IRouteData,
	ISharedData,
	ITypePageCard,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';
import { routeParams } from '../../routeParams.js';
import { ICalendarRouteData } from '../profile/calendar-routes.js';
import { IViewOrEditDietPlanRouteData } from '../trainer/diet-routes.js';
import { IViewOrEditWorkoutPlanRouteData } from '../trainer/workout-routes.js';

interface ILoadClientDataConfig {}

const sharedClientConfig = (config?: ILoadClientDataConfig) => {
	return {
		loadSharedData: getLoadClientData,
		sharedDataHash: getSharedDataHash
	};
};

function getSharedDataHash(config?: ILoadClientDataConfig): (routeData: IRouteData) => string {
	return (routeData) => `client_shared_data_${routeData.primaryId}`;
}

function getLoadClientData(
	config?: ILoadClientDataConfig
): (context: ILoadSharedDataContext) => Observable<ISharedData<UserDb.IUserItem>> {
	return (context) =>
		context.dependencies.httpService
			.get<UserDb.IUserItem>((builder) =>
				builder
					.withCustomApiRoute('user', 'getUser')
					.withQueryString(routeParams.loadProgressTypes, true)
					.withQueryString(routeParams.loadTasks, true)
					.withQueryString(routeParams.loadPackages, true)
					.withId(context.authUser.id)
					.build()
			)
			.pipe(
				map((response) => {
					const shared: ISharedData<UserDb.IUserItem> = {
						data: response.data,
						linkedItems: response.linkedItems ?? []
					};

					return shared;
				})
			);
}

export interface IClientRoutes extends ICoreRoutes {
	dashboard: ITypeRoute<
		UserDb.IUserItem,
		{
			overview: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	training: ITypeRoute<
		UserDb.IUserItem,
		{
			overview: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	nutrition: ITypeRoute<
		UserDb.IUserItem,
		{
			overview: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	calendar: ITypeRoute<
		UserDb.IUserItem,
		{
			upcoming: ITypeRouteAction<ICalendarRouteData>;
			past: ITypeRouteAction<ICalendarRouteData>;
		}
	>;
	progress: ITypeRoute<
		UserDb.IUserItem,
		{
			overview: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	files: ITypeRoute<
		UserDb.IUserItem,
		{
			overview: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	gallery: ITypeRoute<
		UserDb.IUserItem,
		{
			overview: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	tasks: ITypeRoute<
		UserDb.IUserItem,
		{
			overview: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	workout: ITypeRoute<
		IViewOrEditWorkoutPlanRouteData,
		{
			view: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
		}
	>;
	diet: ITypeRoute<
		IViewOrEditDietPlanRouteData,
		{
			view: ITypeRouteAction<IViewOrEditDietPlanRouteData>;
		}
	>;
	sessions: ITypeRoute<
		UserDb.IUserItem,
		{
			manage: ITypeRouteAction<IViewOrEditDietPlanRouteData>;
		}
	>;
}

export const clientRoutes: RouteInitializer<IClientRoutes> = {
	dashboard: {
		mainPath: 'client',
		uniqueId: 'clientDashboard',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			overview: {
				uniqueId: `clientDashboard`,
				component: ClientDashboardComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Dashboard'
					};
					return metadata;
				}
			}
		}
	},
	sessions: {
		uniqueId: `clientSessions`,
		mainPath: 'client',
		parentRoutes: (context) => [],
		menuItems: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		children: {
			manage: {
				uniqueId: `manageClientSessions`,
				component: ClientSessionsComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Sessions`
					};
					return metadata;
				}
			}
		}
	},
	training: {
		mainPath: 'client',
		uniqueId: 'clientTraining',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			overview: {
				uniqueId: `clientTraining`,
				component: ClientTrainingComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Training`,
						card: getTrainingCard(context.sharedData?.data, context.dependencies)
					};
					return metadata;
				}
			}
		}
	},
	nutrition: {
		mainPath: 'client',
		uniqueId: 'clientNutrition',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			overview: {
				uniqueId: `clientNutrition`,
				component: ClientDietComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Nutrition`,
						card: getNutritionCard(context.sharedData?.data, context.dependencies)
					};
					return metadata;
				}
			}
		}
	},
	progress: {
		mainPath: 'client',
		uniqueId: 'clientProgress',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			overview: {
				uniqueId: `clientProgressOverview`,
				component: ClientProgressComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Progress`,
						card: getProgressCard(context.sharedData?.data, context.dependencies)
					};
					return metadata;
				}
			}
		}
	},
	tasks: {
		mainPath: 'client',
		uniqueId: 'clientTasks',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			overview: {
				uniqueId: `clientTasksOverview`,
				component: ClientTasksComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Tasks`,
						card: getProgressCard(context.sharedData?.data, context.dependencies)
					};
					return metadata;
				}
			}
		}
	},
	gallery: {
		mainPath: 'client',
		uniqueId: 'clientGallery',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			overview: {
				uniqueId: `clientGalleryOverview`,
				component: ClientGalleryComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Gallery`,
						card: undefined
					};
					return metadata;
				}
			}
		}
	},
	files: {
		mainPath: 'client',
		uniqueId: 'clientFiles',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			overview: {
				uniqueId: `clientFIlesOverview`,
				component: ClientFilesComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Files`,
						card: undefined
					};
					return metadata;
				}
			}
		}
	},
	calendar: {
		mainPath: 'client',
		uniqueId: 'clientCalendar',
		parentRoutes: (context) => [],
		loadSharedData: sharedClientConfig().loadSharedData(),
		sharedDataHash: sharedClientConfig().sharedDataHash(),
		menuItems: (context) => [],
		children: {
			upcoming: {
				processSharedData: (context) => {
					context.sharedData.data.mode = 'upcoming';
					return context.sharedData;
				},
				uniqueId: `clientCalendarUpcoming`,
				component: CalendarComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Upcoming appointments`,
						card: getCalendarCard(undefined, context.dependencies)
					};

					return metadata;
				}
			},
			past: {
				processSharedData: (context) => {
					context.sharedData.data.mode = 'past';
					return context.sharedData;
				},
				uniqueId: `clientCalendarPast`,
				component: CalendarComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Past appointments`,
						card: getCalendarCard(undefined, context.dependencies)
					};

					return metadata;
				}
			}
		}
	},
	workout: {
		mainPath: 'client',
		uniqueId: 'clientWorkout',
		parentRoutes: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.clientRoutes.training.children.overview,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Training`
			}
		],
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserApi.IUserWithWorkoutResponse>((builder) =>
					builder
						.withCustomApiRoute('user', 'getUserWithWorkout')
						.withPath(
							`/${context.routeData.primaryId}/${context.routeData.secondaryId}/${
								context.routeData.tertiaryId
							}/${context.dependencies.timeService.toISODate(context.dependencies.timeService.now())}`
						)
						.build()
				)
				.pipe(
					map((response) => {
						const container = response.data.user.training?.containers?.find(
							(m) => m.id.toLowerCase() === context.routeData.secondaryId?.toLowerCase()
						);

						if (!container) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[getUserWithWorkout] Invalid container with id '${context.routeData.secondaryId}'`
							});
						}

						const containerItem = container.items.find(
							(m) => m.id.toLowerCase() === context.routeData.tertiaryId?.toLowerCase()
						);

						if (!containerItem) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[getUserWithWorkout] Invalid container item with id '${context.routeData.tertiaryId}'`
							});
						}

						const shared: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
							data: {
								workout: containerItem.workout,
								workoutType: 'workout',
								sessionForDate: response.data.sessionForDate,
								clientData: {
									client: response.data.user,
									trainingContainer: container,
									trainingItem: containerItem
								}
							},
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [],
		children: {
			view: {
				uniqueId: `viewClientWorkout`,
				component: ViewOrEditWorkoutComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `${context.sharedData?.data?.clientData?.trainingContainer?.title}`,
						card: getWorkoutCard(context.sharedData?.data, context.dependencies)
					};
					return metadata;
				},
				processSharedData: (context) => {
					return context.sharedData;
				}
			}
		}
	},
	diet: {
		mainPath: 'client',
		uniqueId: 'clientDiet',
		parentRoutes: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.clientRoutes.nutrition.children.overview,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Nutrition`
			}
		],
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserDb.IUserItem>((builder) =>
					builder
						.withCustomApiRoute('user', 'getUser')
						.withId(context.routeData.primaryId)
						.withQueryString(routeParams.loadDietFoods, true)
						.build()
				)
				.pipe(
					map((response) => {
						const container = response.data.nutrition?.containers?.find(
							(m) => m.id.toLowerCase() === context.routeData.secondaryId?.toLowerCase()
						);

						if (!container) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[clientDiet] Invalid container with id '${context.routeData.secondaryId}'`
							});
						}

						const containerItem = container.items.find(
							(m) => m.id.toLowerCase() === context.routeData.tertiaryId?.toLowerCase()
						);

						if (!containerItem) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[clientDiet] Invalid container item with id '${context.routeData.tertiaryId}'`
							});
						}

						const shared: ISharedData<IViewOrEditDietPlanRouteData> = {
							data: {
								diet: containerItem.diet,
								clientData: {
									client: response.data,
									dietContainer: container,
									dietItem: containerItem
								}
							},
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [],
		children: {
			view: {
				uniqueId: `viewClientDiet`,
				component: ViewOrEditDietComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `${context.sharedData?.data?.clientData?.dietContainer?.title}`,
						card: getDietCard(
							context.sharedData?.data,
							context.dependencies,
							context.sharedData?.linkedItems ?? []
						)
					};
					return metadata;
				},
				processSharedData: (context) => {
					return context.sharedData;
				}
			}
		}
	}
};

function getTrainingCard(
	user: UserDb.IUserItem | undefined,
	dependencies: CoreAppComponentDependencies
): ITypePageCard | undefined {
	return undefined;
}

function getNutritionCard(
	user: UserDb.IUserItem | undefined,
	dependencies: CoreAppComponentDependencies
): ITypePageCard | undefined {
	return undefined;
}

function getCalendarCard(
	user: UserDb.IUserItem | undefined,
	dependencies: CoreAppComponentDependencies
): ITypePageCard | undefined {
	return undefined;
}

function getProgressCard(
	user: UserDb.IUserItem | undefined,
	dependencies: CoreAppComponentDependencies
): ITypePageCard | undefined {
	return undefined;
}

function getDietCard(
	data: IViewOrEditDietPlanRouteData | undefined,
	dependencies: CoreAppComponentDependencies,
	linkedItems: CosmosDb.ICosmosItem[]
): ITypePageCard | undefined {
	if (!data) {
		return undefined;
	}

	const pdfButton: IButton = {
		icon: 'pdf',
		title: `Download diet PDF`,
		buttonType: 'basic',
		click: () => {
			dependencies.dialogService.openPdfDialog({
				generatePdf: () =>
					dependencies.httpService.generateDietPdf({
						diet: data.diet,
						userId: data.clientData?.client.id,
						containerItemId: data.clientData?.dietItem?.id
					})
			});
		}
	};

	const mealsCount: number = data.diet?.foodContainers.length ?? 0;

	return {
		title: { value: stringHelper.getRequiredItemName(data.diet?.system.name) },
		updated: data.diet.system.updated,
		avatar: {
			avatar: data.diet.avatar?.[0]?.image,
			avatarName: data.diet?.system.name
		},
		buttons: [pdfButton],
		tags: [
			{
				icon: 'food',
				text: `${stringHelper.getPluralText({
					value: mealsCount,
					plural0: '0 meals',
					plural1: '1 meal',
					plural2: `${mealsCount} meals`,
					plural5: `${mealsCount} meals`
				})}`
			}
		],
		nutrition: data.diet
			? foodHelper.calculateFoodNutrientsForDiet(
					data.diet,
					linkedItemsHelper.getLinkedItemsByType<FoodDb.IFoodItem>(linkedItems, 'Food')
			  )
			: undefined
	};
}

function getWorkoutCard(
	data: IViewOrEditWorkoutPlanRouteData | undefined,
	dependencies: CoreAppComponentDependencies
): ITypePageCard | undefined {
	const workout = data?.workout;
	if (!data || !workout) {
		return undefined;
	}

	const exercisesCount: number = workout.exerciseContainers.length ?? 0;

	const pdfButton: IButton | undefined =
		workout.type === 'workout'
			? {
					title: `Download workout PDF`,
					buttonType: 'basic',
					click: () => {
						dependencies.dialogService.openPdfDialog({
							generatePdf: () =>
								dependencies.httpService.generateWorkoutPdf({
									workout: workout,
									userId: data.clientData?.client.id,
									containerItemId: data.clientData?.trainingItem?.id
								})
						});
					}
			  }
			: undefined;

	return {
		buttons: [pdfButton].filter(isNotUndefined),
		title: { value: workout.system.name ?? 'n/a' },
		avatar: {
			avatar: workout.avatar?.[0]?.image,
			avatarName: workout.system.name
		},
		updated: workout.system.updated,
		tags:
			workout.type === 'workout'
				? [
						{
							icon: 'dumbbell',
							text: `${stringHelper.getPluralText({
								value: exercisesCount,
								plural0: '0 exercises',
								plural1: '1 exercise',
								plural2: `${exercisesCount} exercises`,
								plural5: `${exercisesCount} exercises`
							})}`
						}
				  ]
				: []
	};
}
